import React from "react";

import { AlertTriangle, ChevronDown, Plus } from "lucide-react";

import {
  ExperimentType,
  contentByExperimentType,
} from "./researchFoundation.constants";

interface KeyAssumptionsSectionProps {
  assumptions: string[];
  onChange: (assumptions: string[]) => void;
  experimentType: ExperimentType;
  defaultOpen?: boolean;
}

export const KeyAssumptionsSection: React.FC<KeyAssumptionsSectionProps> = ({
  assumptions,
  onChange,
  experimentType,
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const config = contentByExperimentType[experimentType];
  const maxAssumptions = 3;

  const handleAssumptionChange = (index: number, value: string) => {
    const next = [...assumptions];
    next[index] = value;
    onChange(next);
  };

  const handleAddAssumption = () => {
    if (assumptions.length < maxAssumptions) onChange([...assumptions, ""]);
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen((p) => !p)}
        className={`w-full rounded-lg border p-4 transition-all duration-300 ${
          isOpen
            ? "border-amber-400/30 bg-amber-400/5"
            : "border-white/20 bg-white/5 hover:border-white/30 hover:bg-white/10"
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-blue-400" />
            <span className="text-sm font-semibold text-white">
              Key Assumptions
            </span>
            <span className="text-xs text-white/40">
              ({assumptions.filter((a) => a.trim()).length}/{maxAssumptions})
            </span>
          </div>
          <ChevronDown
            size={16}
            className={`text-white/60 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="space-y-4 pt-4">
          <p className="text-xs text-white/50">
            What must be true for your hypothesis to succeed? These become risk
            analysis points.
          </p>

          <div className="space-y-3">
            {assumptions.map((assumption, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/60">
                    Assumption {index + 1}
                  </span>
                </div>
                <textarea
                  value={assumption}
                  onChange={(e) =>
                    handleAssumptionChange(index, e.target.value)
                  }
                  placeholder={
                    config.keyAssumptions.placeholders[index] ||
                    "State an assumption that must be true..."
                  }
                  rows={2}
                  className="w-full resize-none rounded-md border border-white/20 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-amber-400/50 focus:outline-none"
                />
              </div>
            ))}
          </div>

          {assumptions.length < maxAssumptions && (
            <button
              type="button"
              onClick={handleAddAssumption}
              className="inline-flex items-center rounded-[6px] px-2 py-1 text-sm text-white/60 hover:bg-white/10 hover:text-white"
            >
              <Plus size={14} className="mr-1" />
              Add assumption
            </button>
          )}
        </div>
      )}
    </div>
  );
};
